import React, { useEffect } from 'react'
import NumberFormat from 'react-number-format'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Wrapper } from '@farewill/ui'
import { BORDER_RADIUS, COLOR, FONT, GTR } from '@farewill/ui/tokens'

import { useFormContext } from '../index'

export const StyledLabel = styled.label`
  margin-bottom: ${GTR.XS};

  ${({ $error }) => $error && `color: ${COLOR.STATE.ERROR};`}
`

export const StyledInput = styled.input`
  width: 100%;
  background-color: ${COLOR.WHITE};
  padding: ${GTR.S};
  border-radius: ${BORDER_RADIUS.S};
  border: 2px solid ${COLOR.GREY.LIGHT};
  outline: 0;
  color: ${COLOR.BLACK};
  transition: border-color 0.2s ease-in-out;

  &:focus {
    border-color: ${COLOR.GREY.MEDIUM};
  }

  ${({ $error }) =>
    $error &&
    `
    border-color: ${COLOR.STATE.ERROR};
    
    &:focus {
        border-color: ${COLOR.STATE.ERROR};
    }
`}

  ${({ $disableResize }) => $disableResize && `resize: none;`}
`

const StyledError = styled.span`
  color: ${COLOR.STATE.ERROR};
  font-size: ${FONT.SIZE.XS};
`

const Input = ({
  label,
  name,
  onKeyPress,
  onKeyDown,
  rows,
  autoFocus,
  tag,
  type,
  required,
  validate,
  isCurrency,
  prefix,
  placeholder,
  ariaAttr,
}) => {
  const { errors, handleChange, setFieldError, setTouched, touched, values } =
    useFormContext()
  const currentValue = values[name]
  const error = errors[name]
  const isTouched = touched[name]
  const hasError = error && isTouched

  const getError = (value) => {
    if (required && !value) return 'This field is required.'
    return validate(value)
  }

  useEffect(() => {
    setFieldError(name, getError(currentValue))
  }, [currentValue])

  return (
    <Wrapper>
      {label && (
        <StyledLabel htmlFor={`input-${name}`} $error={hasError}>
          {label}
        </StyledLabel>
      )}
      {isCurrency ? (
        <NumberFormat
          allowNegative={false}
          customInput={StyledInput}
          onBlur={() => setTouched({ ...touched, [name]: true })}
          onChange={handleChange}
          onKeyPress={onKeyPress}
          onKeyDown={onKeyDown}
          id={`input-${name}`}
          name={name}
          value={currentValue}
          $error={hasError}
          decimalScale={2}
          thousandSeparator
          prefix={prefix}
          inputmode="numeric"
          placeholder={placeholder}
          autoFocus={autoFocus}
          {...ariaAttr} // eslint-disable-line
        />
      ) : (
        <StyledInput
          as={tag}
          onBlur={() => setTouched({ ...touched, [name]: true })}
          onChange={handleChange}
          onKeyPress={onKeyPress}
          onKeyDown={onKeyDown}
          id={`input-${name}`}
          name={name}
          type={type}
          rows={rows}
          value={currentValue}
          $error={hasError}
          $disableResize={tag === 'textarea'}
          placeholder={placeholder}
          autoFocus={autoFocus}
          {...ariaAttr} // eslint-disable-line
        />
      )}
      {hasError && <StyledError>{error}</StyledError>}
    </Wrapper>
  )
}

Input.propTypes = {
  autoFocus: PropTypes.bool,
  label: PropTypes.string,
  onKeyPress: PropTypes.func,
  onKeyDown: PropTypes.func,
  name: PropTypes.string.isRequired,
  required: PropTypes.bool,
  rows: PropTypes.string,
  tag: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
  type: PropTypes.string,
  validate: PropTypes.func,
  isCurrency: PropTypes.bool,
  prefix: PropTypes.string,
  placeholder: PropTypes.string,
  ariaAttr: PropTypes.shape({}),
}

Input.defaultProps = {
  autoFocus: false,
  label: '',
  onKeyPress: () => null,
  onKeyDown: () => null,
  rows: '',
  required: false,
  tag: 'input',
  type: 'text',
  validate: () => null,
  isCurrency: false,
  prefix: '£',
  placeholder: '',
  ariaAttr: {},
}

export default Input
